import {
  serverAmountToClientAmount,
  formatRoutingNumberForServer,
  formatFiatAccountNumberForServer,
  formatCryptoAccountNumberForServer,
} from '@razz-team/utils';
import {
  ApiBeneficiary,
  ApiMoney,
  ApiNewBeneficiary,
  ApiSide,
  ApiTransaction,
} from '../../../openapi';

type ExistingBeneficiary = {
  type: 'existing';
  value: ApiBeneficiary;
};

type NewCryptoBeneficiary = {
  type: 'crypto';
  value: ApiNewBeneficiary;
};

type InternalBeneficiary = {
  type: 'internal';
  value: {
    currency: string;
    receiverId: string;
  };
};

export enum BankBeneficiaryType {
  business,
  individual,
}

type NewBankBeneficiary = {
  type: 'bank';
  value: ApiNewBeneficiary;
  valueType: BankBeneficiaryType;
};

export type TransferBeneficiary =
  | ExistingBeneficiary
  | InternalBeneficiary
  | NewCryptoBeneficiary
  | NewBankBeneficiary;

export interface TransferFormValues {
  accountId: string;
  side: ApiSide | undefined;
  clientAmount: string;
  comment: string;
  reference: string;
  evidence?: string[] | null;
  solution: string | null;
  beneficiary: TransferBeneficiary;
}

export type BankBeneficiaryFormValues = {
  beneficiary: NewBankBeneficiary;
};

export type CryptoBeneficiaryFormValues = {
  beneficiary: NewCryptoBeneficiary;
};

export type InternalBeneficiaryFormValues = {
  beneficiary: InternalBeneficiary['value'];
};

export function trimOrNull(value: string | null | undefined) {
  return value?.replace(/\s+/, ' ').trim() || null;
}

function cleanupNewBankBeneficiary(beneficiary: ApiNewBeneficiary): ApiNewBeneficiary {
  const address = beneficiary.beneficiaryAddress;
  const accountNumber = formatFiatAccountNumberForServer(beneficiary.accountNumber);
  const routingNumber = formatRoutingNumberForServer(beneficiary.routingNumber || '') || null;
  const routingNumber2 = formatRoutingNumberForServer(beneficiary.routingNumber2 || '') || null;

  return {
    accountNumber,
    routingNumber,
    routingNumber2,
    currency: beneficiary.currency,
    businessName: trimOrNull(beneficiary.businessName),
    individualName: trimOrNull(beneficiary.individualName),
    bankName: trimOrNull(beneficiary.bankName),
    bankCountry: beneficiary.bankCountry ?? null,
    bankAddress: trimOrNull(beneficiary.bankAddress),
    beneficiaryAddress: address?.country
      ? {
          country: address.country,
          zipCode: trimOrNull(address.zipCode),
          region: trimOrNull(address.region),
          city: trimOrNull(address.city) || '',
          streetLine1: trimOrNull(address.streetLine1) || '',
          streetLine2: trimOrNull(address.streetLine2),
        }
      : null,
  };
}

function cleanupNewCryptoBeneficiary(beneficiary: ApiNewBeneficiary): ApiNewBeneficiary {
  return {
    currency: beneficiary.currency,
    displayName: trimOrNull(beneficiary.displayName),
    accountNumber: formatCryptoAccountNumberForServer(beneficiary.accountNumber),
    transferNetworkId: beneficiary.transferNetworkId,
    subtype: beneficiary.subtype,
    beneficiaryName: trimOrNull(beneficiary.beneficiaryName),
  };
}

function mapBankBeneficiaryToApi(beneficiary: NewBankBeneficiary): ApiNewBeneficiary {
  return cleanupNewBankBeneficiary(beneficiary.value);
}

function mapCryptoBeneficiaryToApi(beneficiary: NewCryptoBeneficiary): ApiNewBeneficiary {
  return cleanupNewCryptoBeneficiary(beneficiary.value);
}

export function mapTransferBeneficiaryToApi(
  beneficiary: TransferBeneficiary | null | undefined,
): ApiNewBeneficiary | undefined {
  switch (beneficiary?.type) {
    case 'existing':
      return {
        currency: beneficiary.value.currency,
        accountNumber: beneficiary.value.accountNumber?.value,
        businessName:
          beneficiary.value.type === 'BUSINESS' ? beneficiary.value.businessName : undefined,
        individualName:
          beneficiary.value.type === 'INDIVIDUAL' ? beneficiary.value.individualName : undefined,
        routingNumber:
          beneficiary.value.type !== 'CRYPTO' ? beneficiary.value.routingNumber?.value : undefined,
        transferNetworkId: beneficiary.value.transferNetworkId,
        bankCountry:
          beneficiary.value.type !== 'CRYPTO' ? beneficiary.value.bankCountry : undefined,
      };
    case 'bank':
      return mapBankBeneficiaryToApi(beneficiary);
    case 'crypto':
      return mapCryptoBeneficiaryToApi(beneficiary);
    default:
      return undefined;
  }
}

export function mapTransferBeneficiaryToCreateApi(
  beneficiary: ExistingBeneficiary | NewCryptoBeneficiary | NewBankBeneficiary | null | undefined,
): { beneficiary?: null | ApiNewBeneficiary; beneficiaryId?: null | string } {
  switch (beneficiary?.type) {
    case 'existing':
      return {
        beneficiaryId: beneficiary.value.beneficiaryId,
      };
    case 'crypto':
      return {
        beneficiary: cleanupNewCryptoBeneficiary({
          currency: beneficiary.value.currency,
          displayName: beneficiary.value.displayName,
          accountNumber: beneficiary.value.accountNumber,
          transferNetworkId: beneficiary.value.transferNetworkId,
          subtype: beneficiary.value.subtype,
          beneficiaryName: beneficiary.value.beneficiaryName,
        }),
      };
    case 'bank':
      return {
        beneficiary: cleanupNewBankBeneficiary({
          ...beneficiary.value,
          businessName:
            beneficiary.valueType === BankBeneficiaryType.business
              ? beneficiary.value.businessName
              : null,
          individualName:
            beneficiary.valueType === BankBeneficiaryType.individual
              ? beneficiary.value.individualName
              : null,
        }),
      };

    default:
      return {};
  }
}

export function mapExistingBeneficiaryToTransferBeneficiary(
  beneficiary: ApiBeneficiary,
): TransferBeneficiary {
  switch (beneficiary.type) {
    case 'CRYPTO':
      return {
        type: 'crypto',
        value: {
          currency: beneficiary.currency,
          displayName: beneficiary.displayName,
          accountNumber: beneficiary.accountNumber.value,
          transferNetworkId: beneficiary.transferNetworkId,
          subtype: beneficiary.subtype,
          beneficiaryName: beneficiary.beneficiaryName,
        },
      };
    default:
      return {
        type: 'existing',
        value: beneficiary,
      };
  }
}

function isCrypto(beneficiary: TransferBeneficiary | null | undefined) {
  return (
    beneficiary?.type === 'crypto' ||
    (beneficiary?.type === 'existing' && beneficiary.value.type === 'CRYPTO')
  );
}

export function isCommentApplicable(beneficiary: TransferBeneficiary | null | undefined) {
  return beneficiary?.type === 'internal';
}

export function isEvidenceApplicable(beneficiary: TransferBeneficiary | null | undefined) {
  return beneficiary?.type !== 'internal';
}

export function isEvidenceRequired(
  beneficiary: TransferBeneficiary | null | undefined,
  targetAmount: ApiMoney | null | undefined,
) {
  if (isCrypto(beneficiary)) return false;
  if (__NODA__) return true;
  if (!targetAmount) return false;
  const clientAmount = serverAmountToClientAmount(targetAmount);
  return clientAmount >= 10_000;
}

export function isReferenceApplicable(beneficiary: TransferBeneficiary | null | undefined) {
  return (
    beneficiary?.type === 'bank' ||
    (beneficiary?.type === 'existing' && beneficiary.value.type !== 'CRYPTO')
  );
}

export function isPopCodeApplicable(beneficiary: TransferBeneficiary | null | undefined) {
  return (
    beneficiary?.type === 'bank' ||
    (beneficiary?.type === 'existing' && beneficiary.value.type !== 'CRYPTO')
  );
}

export function transferValuesFromTransaction(
  transaction: ApiTransaction,
): Partial<TransferFormValues> | undefined {
  if (transaction.direction !== 'OUT') return undefined;

  const accountId = transaction.origin.account?.id;
  const amount = transaction.counterparty.amountWithoutFee ?? transaction.counterparty.amount;
  const clientAmount = serverAmountToClientAmount(amount).toString();

  if (transaction.counterparty.beneficiary) {
    return {
      accountId,
      clientAmount,
      beneficiary: mapExistingBeneficiaryToTransferBeneficiary(
        transaction.counterparty.beneficiary,
      ),
    };
  }

  if (transaction.counterparty.customer) {
    return {
      accountId,
      clientAmount,
      beneficiary: {
        type: 'internal',
        value: {
          currency: amount.currency,
          receiverId: transaction.counterparty.customer.id,
        },
      },
    };
  }

  return undefined;
}
